import {
  Component,
  Input,
  ViewChild,
  AfterViewInit,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import { MatDialog } from '@angular/material/dialog';
import { DayPartMap, DayPart } from '@interfaces/dayParts/1.0';
import { SelectionModel } from '@angular/cdk/collections';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DayPartsDialogData } from '@interfaces/dayParts/1.0';
import { Tenant } from '@generated/tcea/models';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { ConfirmationComponent } from '@components/confirmation/1.0';
import { BrandService } from '@services/brand/1.0/brand.service';
import { DecoratorService } from '@services/decorator/1.0/decorator.service';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { CreateDayPartsDialogComponent } from '../create-daypart-dialog/create-daypart-dialog.component';
import { Store } from '@interfaces/store/1.0';
import { StoreService } from '@services/store/1.0/store.service';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { NgIf } from '@angular/common';
import { ButtonComponent } from '../../button/1.0/button.component';
import { LoadingComponent } from '../../loading/1.0/loading.component';
import { MessageComponent } from '../../message/1.0/message.component';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'txp-dayparts-list',
  templateUrl: './dayparts-list.component.html',
  styleUrls: ['./dayparts-list.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ButtonComponent,
    LoadingComponent,
    MessageComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
  ],
})
export class DaypartsListComponent implements OnDestroy, AfterViewInit, OnInit {
  @ViewChild(MatSort) sort: MatSort;
  @Input() brand: Tenant;
  @Input() store: Store; // For store-level dayparts
  @Input() menu: any; // For menu-level dayparts
  @Input() isStoreLevel: boolean = false; // Flag to determine store context
  @Input() isMenuLevel: boolean = false; // Flag to determine menu context
  @Input() readOnly: boolean;
  @Output() save = new EventEmitter<DayPartMap>();

  buttonStyle = ButtonStyle;
  buttonSize = ButtonSize;
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  isLoading: boolean;
  loadingColor = LoadingColor;
  messageType = MessageType;
  showError: boolean;
  selection = new SelectionModel(true, [], true);
  dayPartsList: DayPartMap = {};
  brandDayparts: DayPartMap = {}; // Store brand dayparts for store-level context
  private unsubscribe = new Subject<void>();

  get hasBrandDayparts(): boolean {
    return (
      (this.isStoreLevel || this.isMenuLevel) && Object.keys(this.brandDayparts || {}).length > 0
    );
  }

  get canAddDaypart(): boolean {
    if (this.isStoreLevel || this.isMenuLevel) {
      // Can add if there are brand dayparts that haven't been configured at store/menu level
      const brandDaypartIds = Object.keys(this.brandDayparts || {});
      const configuredDaypartIds = Object.keys(this.dayPartsList || {});
      return brandDaypartIds.some((id) => !configuredDaypartIds.includes(id));
    }
    return true; // Brand level can always add
  }

  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService,
    private brandService: BrandService,
    private decoratorService: DecoratorService,
    private storeService: StoreService,
    private permissionsService: PermissionsService,
  ) {
    // Initialize readOnly based on permissions if not explicitly set via @Input
    if (this.readOnly === undefined) {
      this.readOnly = !this.permissionsService.getPermission('menu:write');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  ngOnInit(): void {
    // Load brand dayparts first if in store-level or menu-level context
    if ((this.isStoreLevel || this.isMenuLevel) && this.brand?.id) {
      this.brandService
        .getDayparts(this.brand.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (brandDayparts: DayPartMap) => {
            this.brandDayparts = brandDayparts || {};
            // After loading brand dayparts, load store/menu dayparts
            this.loadDayparts();
          },
          error: (error) => {
            this.brandDayparts = {};
            // Still try to load store/menu dayparts even if brand dayparts fail
            this.loadDayparts();
          },
        });
    } else {
      // For brand level, just load dayparts directly
      this.loadDayparts();
    }
  }

  loadDayparts(): void {
    if (!this.brand?.id) {
      this.showError = false;
      this.showMissingBrandId = true;
      return;
    }

    if (this.isStoreLevel && !this.store?.id) {
      this.showError = false;
      this.showMissingStoreId = true;
      return;
    }

    if (this.isMenuLevel && !this.menu?.menuId) {
      this.showError = false;
      this.showMissingMenuId = true;
      return;
    }

    // Only load if not already loaded
    if (Object.keys(this.dayPartsList).length > 0) {
      return;
    }

    this.isLoading = true;
    this.showError = false;

    if (this.isStoreLevel) {
      // Load store-level dayparts
      this.storeService
        .getStoreDayparts(this.brand.id, this.store.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (dayparts: DayPartMap) => {
            this.dayPartsList = dayparts || {};
            this.dataSource.data = this.setupDataSource(dayparts);
            this.isLoading = false;
          },
          error: (error) => {
            this.showError = true;
            this.isLoading = false;
          },
        });
    } else if (this.isMenuLevel) {
      // Load menu-level dayparts
      this.storeService
        .getMenuDayparts(this.brand.id, this.menu.menuId)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (dayparts: DayPartMap) => {
            this.dayPartsList = dayparts || {};
            this.dataSource.data = this.setupDataSource(dayparts);
            this.isLoading = false;
          },
          error: (error) => {
            this.showError = true;
            this.isLoading = false;
          },
        });
    } else {
      // Load brand-level dayparts
      this.brandService
        .getDayparts(this.brand.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (dayparts: DayPartMap) => {
            // Load decorators for each daypart
            const daypartIds = Object.keys(dayparts);
            if (daypartIds.length === 0) {
              this.dayPartsList = dayparts;
              this.dataSource.data = this.setupDataSource(dayparts);
              this.isLoading = false;
              return;
            }

            // Create an array of decorator requests for all dayparts
            const decoratorRequests = daypartIds.map((daypartId) => {
              return this.decoratorService.get(this.brand.id, daypartId);
            });

            // Load all decorators in parallel
            forkJoin(decoratorRequests)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe({
                next: (decoratorsArray) => {
                  // Map decorators to their respective dayparts
                  daypartIds.forEach((daypartId, index) => {
                    dayparts[daypartId].decorators = decoratorsArray[index];
                  });

                  this.dayPartsList = dayparts;
                  this.dataSource.data = this.setupDataSource(dayparts);
                  this.isLoading = false;
                },
                error: (error) => {
                  // Still show dayparts even if decorators fail to load
                  this.dayPartsList = dayparts;
                  this.dataSource.data = this.setupDataSource(dayparts);
                  this.isLoading = false;
                },
              });
          },
          error: (error) => {
            this.showError = true;
            this.isLoading = false;
          },
        });
    }
  }
  showMissingBrandId: boolean = false;
  showMissingStoreId: boolean = false;
  showMissingMenuId: boolean = false;

  setupDataSource(dayPartsList: DayPartMap) {
    const dayParts = dayPartsList ? Object.keys(dayPartsList) : [];
    if (!dayParts?.length) return [];
    const data = [];
    this.selection.clear();
    for (const key of dayParts) {
      data.push({
        id: key,
        dayPartName: dayPartsList[key]?.name,
        hoursString: this.convertHoursToString(dayPartsList[key]?.hours),
      });
    }
    return data;
  }

  convertHoursToString(hoursTemplate): string {
    const hoursString = [];
    const hours = hoursTemplate ? Object.keys(hoursTemplate) : [];
    if (!hours?.length) return null;
    for (const key of hours) {
      for (const value of hoursTemplate[key]) {
        const time = value.startTime.toString() + ' - ' + value.endTime.toString();
        const idx = hoursString.findIndex((i) => i.timeString === time);
        if (idx < 0) {
          hoursString.push({ timeString: time, dayArr: [key] });
        } else {
          hoursString[idx].dayArr.push(key);
        }
      }
    }
    return this.formatHoursString(hoursString);
  }

  formatHoursString(hoursString) {
    let formatString = '';
    const dayOrder = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    hoursString.forEach((item) => {
      item.dayArr.sort((a, b) => {
        // If not found, put at end
        const aIdx = dayOrder.indexOf(a.trim());
        const bIdx = dayOrder.indexOf(b.trim());
        return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
      });
      item.dayArr = item.dayArr.map((item) => {
        return this.translateService.instant('calendar.shortDays.' + item);
      });
      if (formatString.length) {
        formatString += ', ' + item.dayArr.join(', ') + ': ' + item.timeString;
      } else {
        formatString += item.dayArr.join(', ') + ': ' + item.timeString;
      }
    });
    return formatString;
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      for (const row of this.dataSource.filteredData) {
        this.selection.select(row);
      }
    }
  }

  createDaypart(dayPart?: string) {
    const data = {} as DayPartsDialogData;

    data.brand = this.brand;
    data.store = this.store;
    data.menu = this.menu;
    data.isStoreLevel = this.isStoreLevel;
    data.isMenuLevel = this.isMenuLevel;
    // Convert DayPartMap to array for the dialog
    data.dayPartsList = Object.values(this.dayPartsList || {});
    data.supportedLanguages = this.brand?.supportedLanguages || [];
    data.decorators = [];
    data.action = dayPart ? 'edit' : 'create';

    // For store level, pass brand dayparts for selection
    if (this.isStoreLevel) {
      data.brandDayparts = this.brandDayparts;
      data.storeDayparts = this.dayPartsList;
    }

    // For menu level, pass brand dayparts for selection
    // Filter out already used dayparts (each brand daypart can only be used once at menu level)
    if (this.isMenuLevel) {
      const usedDaypartIds = Object.keys(this.dayPartsList || {});
      data.brandDayparts = Object.keys(this.brandDayparts || {})
        .filter((daypartId) => !usedDaypartIds.includes(daypartId) || daypartId === dayPart)
        .reduce((filtered, daypartId) => {
          filtered[daypartId] = this.brandDayparts[daypartId];
          return filtered;
        }, {} as DayPartMap);
      data.menuDayparts = this.dayPartsList;
    }

    this.openDialog(data, dayPart);
  }

  openDialog(data: DayPartsDialogData, dayPart?: string) {
    if (dayPart?.length) {
      const titleKey = this.isStoreLevel
        ? 'store.dayparts.title'
        : this.isMenuLevel
          ? 'menu.information.dayParts.editDayPart'
          : 'menu.information.dayParts.editDayPart';
      data.title = this.translateService.instant(titleKey);
      data.dayPart = this.dayPartsList[dayPart];

      if (!this.isStoreLevel && !this.isMenuLevel) {
        data.decorators = (data.dayPart?.decorators || []).filter((decorator) => {
          return decorator.entityId === data.dayPart.id;
        });
      }
    } else {
      const titleKey = this.isStoreLevel
        ? 'store.dayparts.addDayPartBtn'
        : this.isMenuLevel
          ? 'menu.information.dayParts.createDayPart'
          : 'menu.information.dayParts.createDayPart';
      data.title = this.translateService.instant(titleKey);
    }
    const dialogRef = this.dialog.open(CreateDayPartsDialogComponent, {
      data: data,
    });
    dialogRef.afterClosed().subscribe((dayPart: DayPart) => {
      if (dayPart) {
        // Update the dayPartsList with the new/updated daypart
        const updatedDayPartsList = { ...this.dayPartsList };
        updatedDayPartsList[dayPart.id] = dayPart;
        this.addDaypartToForm(updatedDayPartsList);
      }
    });
  }

  addDaypartToForm(data: DayPartMap) {
    this.dayPartsList = data;
    this.dataSource.data = this.setupDataSource(data);
    // Emit the save event for parent component to handle
    this.save.emit(data);
  }

  deleteDayparts() {
    let itemsList = '';
    for (const item of this.selection.selected) {
      itemsList = itemsList + item.dayPartName.toUpperCase() + '<br><br>';
    }
    let modalContent = {
      title: this.translateService.instant('menu.information.dayParts.deleteDayParts'),
      message: `${this.translateService.instant(
        'menu.information.dayParts.deleteDayPartsMsg',
      )}<br><br>${itemsList}`,
      cancelText: this.translateService.instant('form.button.cancel'),
      confirmText: this.translateService.instant('form.button.continue'),
    };

    const dialogRef = this.dialog.open(ConfirmationComponent, {
      data: modalContent,
    });

    dialogRef.afterClosed().subscribe((confirmAction: boolean) => {
      const dayParts = { ...this.dayPartsList };
      if (confirmAction) {
        this.selection.selected.forEach((item) => delete dayParts[item.id]);
        this.dayPartsList = dayParts;
        this.dataSource.data = this.setupDataSource(dayParts);
        // Emit the save event for parent component to handle
        this.save.emit(dayParts);
      } else {
        this.selection.clear();
      }
    });
  }
}
